import type { Grade, Profile, Settings } from "./types";
import { emptyLocker, type LockerState } from "./cosmetics";

const KEY = "ft_v1";

export const DEFAULT_SETTINGS: Settings = {
  mute: false,
  reducedMotion: false,
  highContrast: false,
  largeType: false,
  dyslexia: false,
  extraTime: false,
  colorBlind: false,
  sharedDevice: false,
  grade: 3,
  readAloud: false,
};

export interface FTStore {
  settings: Settings;
  profiles: Profile[];
  activeProfileId: string | null;
  guestStars: Record<string, number[]>;
  guestDaily: Record<string, { date: string; score: number; timeMs: number; stars: number }>;
  localScores: Array<{
    mode: string;
    grade: number;
    score: number;
    timeMs: number;
    stars: number;
    date: string;
    nickname: string;
    misses: number;
  }>;
  classCode: string;
  className: string;
  teacherCode: string;
  locker: LockerState;
}

function empty(): FTStore {
  return {
    settings: { ...DEFAULT_SETTINGS },
    profiles: [],
    activeProfileId: null,
    guestStars: {},
    guestDaily: {},
    localScores: [],
    classCode: "",
    className: "Class",
    teacherCode: "",
    locker: emptyLocker(),
  };
}

export function loadStore(): FTStore {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return empty();
    const p = JSON.parse(raw) as FTStore;
    p.settings = { ...DEFAULT_SETTINGS, ...(p.settings || {}) };
    p.profiles = p.profiles || [];
    p.guestStars = p.guestStars || {};
    p.guestDaily = p.guestDaily || {};
    p.localScores = p.localScores || [];
    p.locker = { ...emptyLocker(), ...(p.locker || {}) };
    p.locker.unlocked = Array.from(new Set([...(p.locker.unlocked || []), "pip", "cap", "nib"]));
    return p;
  } catch {
    return empty();
  }
}

export function saveStore(s: FTStore): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* quota */
  }
}

export function starsKey(grade: Grade): string {
  return String(grade);
}

export function getStars(s: FTStore, grade: Grade): number[] {
  const arr = s.guestStars[starsKey(grade)];
  return arr ? arr.slice() : Array(10).fill(0);
}

export function setStar(s: FTStore, grade: Grade, level: number, stars: number): FTStore {
  const k = starsKey(grade);
  const arr = getStars(s, grade);
  const i = level - 1;
  arr[i] = Math.max(arr[i] || 0, stars);
  s.guestStars[k] = arr;
  return s;
}

export function unlockedLevel(s: FTStore, grade: Grade): number {
  const arr = getStars(s, grade);
  let u = 1;
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] > 0) u = i + 2;
  }
  return Math.min(10, u);
}

export function applyDomSettings(settings: Settings): void {
  const r = document.documentElement;
  r.classList.toggle("ft-hc", settings.highContrast);
  r.classList.toggle("ft-lg", settings.largeType);
  r.classList.toggle("ft-dx", settings.dyslexia);
  r.classList.toggle("ft-rm", settings.reducedMotion);
  r.classList.toggle("ft-cb", settings.colorBlind);
}

export function newId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

export function makeClassCode(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let s = "";
  for (let i = 0; i < 6; i++) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return s;
}
